/**
 * Feature access control (multi-tenant, role + plan-module aware)
 *
 * The panel serves admin (1) as a global console and organization (2),
 * client (4) and organization-manager (5) as tenant-scoped self-service
 * portals. Staff (3) manage nothing here (they use the softphone) and get
 * only their own profile.
 *
 * A feature is visible to a non-admin when the caller's role is explicitly
 * allowed OR the caller's organization plan includes the gating module —
 * faithfully replicating the legacy Metronic frontend
 * (AsideMenuMain / PrivateRoutes). The backend independently scopes every
 * read to the caller's organization(s) and (after the #76 hardening)
 * validates writes, so this layer is UX + defence-in-depth, never the sole
 * guard.
 */

import { ROLE_IDS, MODULE_IDS } from '@/config/constants';
import { getRoleId, getPlanModules } from '@/utils/auth';

/** A resource/route name mapped to its gating rule. */
interface AccessRule {
  /** Visible to every authenticated user regardless of role/plan. */
  anyone?: boolean;
  /** Role ids explicitly allowed (admin is always allowed implicitly). */
  roles?: number[];
  /** Plan module ids that unlock the feature for a non-admin. */
  modules?: number[];
}

/**
 * Gating rules keyed by resource/route name. Mirrors the legacy frontend:
 * - organizations: client owns them, or plan has the organization module
 * - departments/staff/mapping: client/manager, or the matching plan module
 * - qrcodes: manager, or the QR plan module
 * - caller fields + all catalogue/system views: admin only
 */
const FEATURE_ACCESS: Record<string, AccessRule> = {
  dashboard: { anyone: true },
  profile: { anyone: true },
  callDetails: { anyone: true },
  organizations: {
    roles: [ROLE_IDS.CLIENT],
    modules: [MODULE_IDS.ORGANIZATION],
  },
  departments: {
    roles: [ROLE_IDS.CLIENT, ROLE_IDS.ORG_MANAGER],
    modules: [MODULE_IDS.SCHEDULES],
  },
  staff: {
    roles: [ROLE_IDS.CLIENT, ROLE_IDS.ORG_MANAGER],
    modules: [MODULE_IDS.STAFF],
  },
  userGroupOrganization: {
    roles: [ROLE_IDS.CLIENT, ROLE_IDS.ORG_MANAGER],
    modules: [MODULE_IDS.STAFF],
  },
  qrcodes: {
    roles: [ROLE_IDS.ORG_MANAGER],
    modules: [MODULE_IDS.QRCODES],
  },
  // Read-only connection (login/logout) audit, scoped by the backend to the
  // caller's organization(s). Same gating as QR codes (login activity of the
  // agents behind them), plus the organization-manager.
  connections: {
    roles: [ROLE_IDS.ORG_MANAGER],
    modules: [MODULE_IDS.QRCODES],
  },
  // Organization-manager (role 5) management: a client provisions the managers
  // of the organizations it owns; unlocked by the organization plan module.
  orgManagers: {
    roles: [ROLE_IDS.CLIENT],
    modules: [MODULE_IDS.ORGANIZATION],
  },
  // Admin-only catalogue / configuration surfaces.
  qrUserDetails: { roles: [] },
  clients: { roles: [] },
  // Prospective clients belong to no tenant yet, so there is nothing to scope
  // them by: the tray is administration-only, never tenant self-service.
  signupRequests: { roles: [] },
  admins: { roles: [] },
  plans: { roles: [] },
  transactions: { roles: [] },
  system: { roles: [] },
  'server-settings': { roles: [] },
};

/**
 * Whether the current user may access a feature (resource or custom route).
 * Reads role + plan modules synchronously from localStorage, so it is safe
 * to call from render (menu filtering, resource gating, route guards).
 * Unknown features default to admin-only (deny for non-admins).
 */
export const canAccessFeature = (feature: string): boolean => {
  const roleId = getRoleId();

  // Admin is the global console: always allowed.
  if (roleId === ROLE_IDS.ADMIN) return true;

  const rule = FEATURE_ACCESS[feature];
  if (!rule) return false; // unknown → admin-only

  if (rule.anyone) return true;
  if (roleId != null && rule.roles?.includes(roleId)) return true;

  if (rule.modules?.length) {
    const modules = getPlanModules();
    if (rule.modules.some((m) => modules.includes(m))) return true;
  }

  return false;
};

/** True when the current user is the global admin (role 1). */
export const isAdmin = (): boolean => getRoleId() === ROLE_IDS.ADMIN;
